import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { 
  Sheet,
  SheetContent,
  SheetTrigger,
} from './ui/sheet';
import { FileText, Users, User, FolderOpen, LogOut, UserCircle, BarChart3, ChevronDown, Building, Factory, UserCheck, Menu, Upload, ClipboardCheck, Truck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from './AuthProvider';
import { isAdminEmail } from '@/lib/adminConfig';

export const Navigation: React.FC = () => {
  const location = useLocation();
  const { user, signOut, isBOD, isSales } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = isAdminEmail(user?.email);
  const canSeeShipment = isAdmin || isBOD || isSales;

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Nav item for all authenticated users
  const myReportNavItem = {
    href: '/send-report',
    label: 'Báo cáo của tôi',
    icon: FileText,
    active: location.pathname === '/send-report',
  };

  const shipmentNavItem = {
    href: '/shipment-plan',
    label: 'Shipment Plan',
    icon: Truck,
    active: location.pathname.startsWith('/shipment-plan'),
  };

  // Main navigation items
  // Order: Report Summary → Reports → Shipment Plan → Sản xuất → Báo cáo của tôi
  const canSeeAllReports = isAdmin || isBOD; // BOD đọc hết: Summary + Reports
  const mainNavItems = [
    ...(canSeeAllReports ? [
      {
        href: '/report-summary',
        label: 'Report Summary',
        icon: BarChart3,
        active: location.pathname === '/report-summary'
      },
      {
        href: '/reports',
        label: 'Reports',
        icon: FileText,
        active: location.pathname === '/reports'
      },
    ] : []),
    ...(canSeeShipment ? [shipmentNavItem] : []),
    ...(isAdmin ? [
      {
        href: '/production',
        label: 'Sản xuất',
        icon: Factory,
        active: location.pathname === '/production'
      },
    ] : []),
    ...(!isBOD ? [myReportNavItem] : []),
  ];

  // HR dropdown menu items
  const hrMenuItems = [
    {
      href: '/hr',
      label: 'Dashboard',
      icon: UserCheck,
      active: location.pathname === '/hr'
    },
    {
      href: '/team-members',
      label: 'Quản lý nhân viên',
      icon: Users,
      active: location.pathname === '/team-members'
    },
    {
      href: '/employee-evaluations',
      label: 'Đánh giá nhân viên',
      icon: ClipboardCheck,
      active: location.pathname === '/employee-evaluations'
    }
  ];

  // Check if any HR menu item is active
  const isHRActive = hrMenuItems.some(item => item.active);

  // All navigation items (now in user dropdown)
  const allNavItems = [
    {
      href: '/report-groups',
      label: 'Report Groups',
      icon: FolderOpen,
      active: location.pathname === '/report-groups'
    },
    {
      href: '/teams',
      label: 'Teams',
      icon: Users,
      active: location.pathname === '/teams'
    },
    {
      href: '/users',
      label: 'Users',
      icon: User,
      active: location.pathname === '/users'
    },
    {
      href: '/companies',
      label: 'Companies',
      icon: Building,
      active: location.pathname === '/companies'
    },
    {
      href: '/upload-demo',
      label: 'Upload Demo',
      icon: Upload,
      active: location.pathname === '/upload-demo'
    }
  ];

  return (
    <nav className="bg-background border-b flex-shrink-0">
      <div className="max-w-7xl mx-auto px-3 sm:px-6">
        {/* Mobile Layout: Hamburger Menu, Logo, User Dropdown */}
        <div className="sm:hidden flex items-center justify-between h-16">
          {/* Hamburger Menu */}
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-4 flex flex-col">
              {/* Logo in Sheet */}
              <div className="flex items-center gap-2 mb-6">
                <img
                  src="https://ecotechdata.s3.ap-southeast-1.amazonaws.com/Logo/email_logo.png"
                  alt="EcoTech Logo"
                  className="h-8 w-auto object-contain"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = 'none';
                    const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
                <h1 className="text-lg font-bold">Report Vault</h1>
              </div>

              {/* Main Navigation Items in Sheet */}
              <nav className="flex flex-col gap-2">
                {mainNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.href} to={item.href} onClick={() => setMobileOpen(false)}>
                      <Button
                        variant={item.active ? "default" : "ghost"}
                        className={cn(
                          "w-full justify-start flex items-center gap-2",
                          item.active && "bg-primary text-primary-foreground"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
                
                {/* HR Dropdown in Mobile — admin only */}
                {isAdmin && (
                  <div className="space-y-1">
                    <div className="text-xs font-semibold text-gray-500 px-2 py-1">Nhân sự</div>
                    {hrMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link key={item.href} to={item.href} onClick={() => setMobileOpen(false)}>
                          <Button
                            variant={item.active ? "default" : "ghost"}
                            className={cn(
                              "w-full justify-start flex items-center gap-2 pl-4",
                              item.active && "bg-primary text-primary-foreground"
                            )}
                          >
                            <Icon className="h-4 w-4" />
                            {item.label}
                          </Button>
                        </Link>
                      );
                    })}
                  </div>
                )}
                
                {isAdmin && <div className="border-t my-2" />}
                {/* Admin-only dropdown items in Sheet */}
                {isAdmin && allNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.href} to={item.href} onClick={() => setMobileOpen(false)}>
                      <Button
                        variant={item.active ? "default" : "ghost"}
                        className={cn(
                          "w-full justify-start flex items-center gap-2",
                          item.active && "bg-accent text-accent-foreground"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Button>
                    </Link>
                  );
                })}
              </nav>

              <div className="mt-auto pt-4 border-t">
                <div className="flex items-center gap-2 mb-2">
                  <UserCircle className="h-5 w-5 text-gray-500" />
                  <div className="flex flex-col space-y-0.5">
                    <p className="text-sm font-medium">{user?.email?.split('@')[0] || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                  </div>
                </div>
                <Button
                  onClick={() => { handleSignOut(); setMobileOpen(false); }}
                  variant="ghost"
                  className="w-full justify-start flex items-center gap-2 text-red-600 hover:text-red-700"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Đăng xuất</span>
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          {/* Logo Section - Mobile (centered) */}
          <div className="flex-grow flex justify-center">
            <img
              src="https://ecotechdata.s3.ap-southeast-1.amazonaws.com/Logo/email_logo.png"
              alt="EcoTech Logo"
              className="h-8 w-auto object-contain"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <h1 className="text-lg font-bold hidden">Report Vault</h1>
          </div>

          {/* User Dropdown Menu - Mobile (right) */}
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1 px-2"
                >
                  <UserCircle className="h-4 w-4" />
                  <span className="text-xs">Me</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <UserCircle className="h-5 w-5 text-gray-500" />
                    <div className="flex flex-col space-y-0.5">
                      <p className="text-sm font-medium">{user?.email?.split('@')[0] || 'User'}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>
                <DropdownMenuSeparator />
                {/* BOD: "Báo cáo của tôi" + Users in user dropdown */}
                {isBOD && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/send-report"
                        className={cn(
                          "flex items-center gap-2 w-full cursor-pointer",
                          myReportNavItem.active && "bg-accent text-accent-foreground"
                        )}
                      >
                        <FileText className="h-4 w-4" />
                        Báo cáo của tôi
                      </Link>
                    </DropdownMenuItem>
                    {!isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/users"
                          className={cn(
                            "flex items-center gap-2 w-full cursor-pointer",
                            location.pathname === '/users' && "bg-accent text-accent-foreground"
                          )}
                        >
                          <User className="h-4 w-4" />
                          Users
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                {/* Admin-only dropdown items — mobile */}
                {isAdmin && allNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center gap-2 w-full cursor-pointer",
                          item.active && "bg-accent text-accent-foreground"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}
                {(isBOD || isAdmin) && <DropdownMenuSeparator />}
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  <span>Đăng xuất</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Desktop Layout: Logo and navigation in same row */}
        <div className="hidden sm:flex items-center justify-between h-16">
          <div className="flex items-center space-x-8">
            <div className="flex-shrink-0">
              <img 
                src="https://ecotechdata.s3.ap-southeast-1.amazonaws.com/Logo/email_logo.png" 
                alt="EcoTech Logo" 
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                  if (fallback) fallback.style.display = 'block';
                }}
              />
              <h1 className="text-xl font-bold hidden">Report Vault</h1>
            </div>
            
            {/* Navigation */}
            <div className="flex space-x-4">
              {/* Main navigation items */}
              {mainNavItems.map((item) => {
                const Icon = item.icon;
                const shortLabel = item.label === 'Report Summary' ? 'Summary' : item.label;
                return (
                  <Link key={item.href} to={item.href}>
                    <Button
                      variant={item.active ? "default" : "ghost"}
                      size="sm"
                      className={cn(
                        "flex items-center gap-2 px-4",
                        item.active && "bg-primary text-primary-foreground"
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="text-sm">{shortLabel}</span>
                    </Button>
                  </Link>
                );
              })}
              
              {/* HR Dropdown Menu — admin only */}
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant={isHRActive ? "default" : "ghost"}
                      size="sm"
                      className={cn(
                        "flex items-center gap-2 px-4",
                        isHRActive && "bg-primary text-primary-foreground"
                      )}
                    >
                      <UserCheck className="h-4 w-4" />
                      <span className="text-sm">Nhân sự</span>
                      <ChevronDown className="h-3 w-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {hrMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <DropdownMenuItem key={item.href} asChild>
                          <Link
                            to={item.href}
                            className={cn(
                              "flex items-center gap-2 w-full cursor-pointer",
                              item.active && "bg-accent text-accent-foreground"
                            )}
                          >
                            <Icon className="h-4 w-4" />
                            {item.label}
                          </Link>
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
          
          {/* User Dropdown Menu - Desktop */}
          <div className="flex items-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-2 px-3"
                >
                  <UserCircle className="h-4 w-4" />
                  <span className="text-sm">{user?.email?.split('@')[0] || 'User'}</span>
                  <ChevronDown className="h-3 w-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    <UserCircle className="h-5 w-5 text-gray-500" />
                    <div className="flex flex-col space-y-0.5">
                      <p className="text-sm font-medium">{user?.email?.split('@')[0] || 'User'}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                  </div>
                </div>
                <DropdownMenuSeparator />

                {/* BOD: "Báo cáo của tôi" + Users in user dropdown */}
                {isBOD && (
                  <>
                    <DropdownMenuItem asChild>
                      <Link
                        to="/send-report"
                        className={cn(
                          "flex items-center gap-2 w-full cursor-pointer",
                          myReportNavItem.active && "bg-accent text-accent-foreground"
                        )}
                      >
                        <FileText className="h-4 w-4" />
                        Báo cáo của tôi
                      </Link>
                    </DropdownMenuItem>
                    {!isAdmin && (
                      <DropdownMenuItem asChild>
                        <Link
                          to="/users"
                          className={cn(
                            "flex items-center gap-2 w-full cursor-pointer",
                            location.pathname === '/users' && "bg-accent text-accent-foreground"
                          )}
                        >
                          <User className="h-4 w-4" />
                          Users
                        </Link>
                      </DropdownMenuItem>
                    )}
                  </>
                )}

                {/* Admin-only dropdown items — desktop */}
                {isAdmin && allNavItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem key={item.href} asChild>
                      <Link
                        to={item.href}
                        className={cn(
                          "flex items-center gap-2 w-full cursor-pointer",
                          item.active && "bg-accent text-accent-foreground"
                        )}
                      >
                        <Icon className="h-4 w-4" />
                        {item.label}
                      </Link>
                    </DropdownMenuItem>
                  );
                })}

                {(isBOD || isAdmin) && <DropdownMenuSeparator />}
                <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
                  <LogOut className="h-4 w-4 mr-2" />
                  <span>Đăng xuất</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </nav>
  );
};